import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_URL from './config';
import savedPatientsData from './savedPatients.json';
import Footer from './components/Footer';
import SelectedPatient from './components/SelectedPatient';
import { 
  DocumentTextIcon,
  BeakerIcon,
  UserIcon,
  ClockIcon,
  DocumentMagnifyingGlassIcon,
  BeakerIcon as MedicationIcon,
  ChartBarIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ClipboardIcon,
  IdentificationIcon
} from '@heroicons/react/24/outline';
import { XMarkIcon as XMarkIconSolid } from '@heroicons/react/20/solid';
import { format } from 'date-fns';
import { ClipboardDocumentIcon } from '@heroicons/react/20/solid';
import BulkDataExport from './components/BulkDataExport';
import InputBuilder from './components/InputBuilder';
import PatientDashboard from './components/PatientDashboard';
import HL7ConnectorManager from './components/HL7ConnectorManager';

function MainApp() {
  const location = useLocation();
  const [patientId, setPatientId] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(() => {
    // Initialize from localStorage if available
    const savedPatient = localStorage.getItem('selectedPatient');
    return savedPatient ? JSON.parse(savedPatient) : null;
  });
  const [selectedFunction, setSelectedFunction] = useState('patient');
  const [selectedCategory, setSelectedCategory] = useState('laboratory');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedObservation, setSelectedObservation] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [dateFilterType, setDateFilterType] = useState('none'); // 'none', 'single', 'range', 'preset'
  const [patientName, setPatientName] = useState('');
  const [birthDate, setBirthDate] = useState(null);
  const [gender, setGender] = useState('');
  const [medicationStatus, setMedicationStatus] = useState('');
  const [observationCategory, setObservationCategory] = useState('laboratory');
  const [conditionCategory, setConditionCategory] = useState('problem-list-item');
  const [medicationDetails, setMedicationDetails] = useState(null);
  const [loadingMedication, setLoadingMedication] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [reportObservations, setReportObservations] = useState([]);
  const [patientEncounters, setPatientEncounters] = useState([]);
  const [practitionerRoles, setPractitionerRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [selectedJsonPaths, setSelectedJsonPaths] = useState(new Set());
  const [inputBuilderPaths, setInputBuilderPaths] = useState(new Set());
  const [foundSystemValue, setFoundSystemValue] = useState(null); // Store the found system value

  // Load inputBuilderPaths from localStorage on mount and when storage changes
  useEffect(() => {
    const loadInputBuilderPaths = () => {
      const savedInputs = JSON.parse(localStorage.getItem('mlInputs') || '[]');
      const paths = new Set(savedInputs.map(input => input.path));
      setInputBuilderPaths(paths);
    };

    // Load paths initially
    loadInputBuilderPaths();

    // Listen for storage changes
    const handleStorageChange = (e) => {
      if (e.key === 'mlInputs' && e.newValue !== e.oldValue) {
        loadInputBuilderPaths();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Update localStorage when selectedPatient changes
  useEffect(() => {
    if (selectedPatient) {
      localStorage.setItem('selectedPatient', JSON.stringify(selectedPatient));
      setPatientId(selectedPatient.id);
    } else {
      localStorage.removeItem('selectedPatient');
      setPatientId('');
    }
  }, [selectedPatient]);

  const functions = [
    { value: 'patient', label: 'Get Patient', icon: UserIcon, isBundle: false },
    { value: 'practitioner', label: 'Get Practitioner', icon: IdentificationIcon, isBundle: false },
    { value: 'observations', label: 'Get Patient Observations', icon: BeakerIcon, isBundle: true },
    { value: 'documents', label: 'Get Document References', icon: DocumentTextIcon, isBundle: true },
    { value: 'medications', label: 'Get Medications', icon: MedicationIcon, isBundle: true },
    { value: 'diagnostic-reports', label: 'Get Diagnostic Reports', icon: ChartBarIcon, isBundle: true },
    { value: 'encounters', label: 'Get Patient Encounters', icon: ClockIcon, isBundle: true },
    { value: 'immunizations', label: 'Get Patient Immunizations', icon: DocumentMagnifyingGlassIcon, isBundle: true },
    { value: 'conditions', label: 'Get Patient Conditions', icon: ExclamationTriangleIcon, isBundle: true },
    { value: 'questionnaire-responses', label: 'Get Questionnaire Responses', icon: ClipboardIcon, isBundle: true }
  ];

  const observationCategories = [
    { value: 'exam', label: 'Audiology' },
    { value: 'functional-mental-status', label: 'Acitivities of Daily Living' },
    { value: 'survey', label: 'Assessments' },
    { value: 'core-characteristics', label: 'Core Characteristics' },
    { value: 'imaging', label: 'DICOM Images' },
    { value: 'social-history', label: 'Family Situation' },
    { value: 'genomic', label: 'Genomic' },
    { value: 'labor-delivery', label: 'Labor & Delivery' },
    { value: 'laboratory', label: 'Laboratory' },
    { value: '252275004-Hematology', label: 'Labs - Hematology' },
    { value: '275711006-Chemistry', label: 'Labs - Chemistry' },
    { value: '68793005-Serology', label: 'Labs - Serology' },
    { value: '395124008-Virology', label: 'Labs - Virology' },
    { value: '314076009-Toxicology', label: 'Labs - Toxicology' },
    { value: '19851009-Microbiology', label: 'Labs - Microbiology' },
    { value: '405825005-Molecular genetics', label: 'Labs - Molecular genetics' },
    { value: 'LDA', label: 'Lines, Drains, Airways' },
    { value: 'newborn-delivery', label: 'Newborn Delivery' },
    { value: 'obstetrics-gynecology', label: 'Obstetrics & Gynecology' },
    { value: 'periodontal', label: 'Periodontal' },
    { value: 'smartdata', label: 'SmartData' },
    { value: 'vital-signs', label: 'Vitals' },
  ];

  const medicationStatuses = [
    { value: '', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'on-hold', label: 'On Hold' },
    { value: 'completed', label: 'Completed' },
    { value: 'stopped', label: 'Stopped' }
  ];

  const conditionCategories = [
    { value: '', label: 'All Categories' },
    { value: 'care-plan-problem', label: 'Care Plan Problem' },
    { value: 'dental-finding', label: 'Dental Finding' },
    { value: 'encounter-diagnosis', label: 'Encounter Diagnosis' },
    { value: 'genomics', label: 'Genomics' },
    { value: 'health-concern', label: 'Health Concern' },
    { value: 'problem-list-item', label: 'Problem List Item' },
    { value: 'infection', label: 'Infection' },
    { value: 'medical-history', label: 'Medical History' },
    { value: 'reason-for-visit', label: 'Reason For Visit' },
  ];

  const handleDateFilter = (type) => {
    setDateFilterType(type);
    const today = new Date();
    
    switch (type) {
      case 'none':
        setDateRange([null, null]);
        break;
      case 'today':
        setDateRange([today, today]);
        break;
      case 'week':
        const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        setDateRange([lastWeek, today]);
        break;
      case 'month':
        const lastMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        setDateRange([lastMonth, today]);
        break;
      default:
        break;
    }
  };

  const handleFunctionChange = (e) => {
    // Reset all relevant state when function changes
    setSelectedFunction(e.target.value);
    setResult(null);
    setError('');
    setDateRange([null, null]);
    setSelectedObservation(null);
    setShowModal(false);
    setSelectedJsonPaths(new Set()); // Reset selected JSON paths
    setReportObservations([]); // Reset report observations
    setPatientEncounters([]); // Reset patient encounters
    setMedicationDetails(null); // Reset medication details
    setCopiedId(null); // Reset copied ID
    setDateFilterType('none'); // Reset date filter type
    setObservationCategory('laboratory'); // Reset observation category
    setConditionCategory('problem-list-item'); // Reset condition category
    setMedicationStatus(''); // Reset medication status
  };

  const handleDateChange = (e) => {
    const date = e.target.value;
    // Already in YYYY-MM-DD format from the input
    setBirthDate(date);
  };

  const handleCopyId = (id) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000); // Reset after 2 seconds
  };

  const handleSelectPatient = async (patient) => {
    // Handle both FHIR format and our saved patients format
    const formattedPatient = {
      id: patient.id,
      name: patient.name?.[0]?.given ? 
        patient.name[0].given.join(' ') + ' ' + patient.name[0].family : 
        patient.name || 'Unknown Name'
    };
    setSelectedPatient(formattedPatient);
    setPatientId(formattedPatient.id);
  };

  const handleClearPatient = () => {
    setSelectedPatient(null);
    setPatientId('');
  };


  // Helper function to extract specific array index from JSON line
  const findSystemValueForClickedElement = (json, lineIndex, path) => {
    return { systemValue: null }; // Simplified - we don't need this anymore
  };

  const handleJsonLineClick = (path, lineIndex) => {
    if (!result) return;

    const lines = JSON.stringify(result, null, 2).split('\n');
    const isBundle = result.resourceType === 'Bundle';
    const adjustedLineIndex = isBundle ? lineIndex + 11 : lineIndex;
    
    // Look up and down for structural elements or system
    let foundSystem = null;
    let foundPath = null;
    
    // Search up to 5 lines in each direction
    for (let i = 0; i < 5; i++) {
      // Look up
      const upIndex = adjustedLineIndex - i;
      if (upIndex >= 0) {
        const upLine = lines[upIndex];
        // Look for both "system" and "reference" fields
        if (upLine.includes('"system":') || upLine.includes('"reference":')) {
          const systemMatch = upLine.match(/"system":\s*"([^"]+)"/) || upLine.match(/"reference":\s*"([^"]+)"/);
          if (systemMatch) {
            foundSystem = systemMatch[1];
            foundPath = path;
            break;
          }
        }
      }
      
      // Look down
      const downIndex = adjustedLineIndex + i;
      if (downIndex < lines.length) {
        const downLine = lines[downIndex];
        // Look for both "system" and "reference" fields
        if (downLine.includes('"system":') || downLine.includes('"reference":')) {
          const systemMatch = downLine.match(/"system":\s*"([^"]+)"/) || downLine.match(/"reference":\s*"([^"]+)"/);
          if (systemMatch) {
            foundSystem = systemMatch[1];
            foundPath = path;
            break;
          }
        }
      }
    }

    if (foundSystem) {
      console.log('✅ Found system/reference:', foundSystem);
      console.log('   Path:', foundPath);
      setFoundSystemValue(foundSystem); // Store the found system value
      setSelectedJsonPaths(new Set([foundPath]));
    } else {
      console.log('❌ No system/reference found nearby');
      setFoundSystemValue(null); // Clear the stored system value
      setSelectedJsonPaths(new Set());
    }
  };

  const addToInputBuilder = (path, lineIndex) => {
    // Get existing inputs from localStorage
    const savedInputs = JSON.parse(localStorage.getItem('mlInputs') || '[]');
    
    // Map the selected function to a valid function ID
    const functionMap = {
      'patient': 'getPatient',
      'practitioner': 'getPractitioner',
      'observations': 'getObservations',
      'documents': 'getDocuments',
      'diagnostic-reports': 'getDiagnosticReports',
      'encounters': 'getEncounters',
      'conditions': 'getPatientConditions',
      'immunizations': 'getPatientImmunizations',
      'medications': 'getMedications'
    };

    const validFunction = functionMap[selectedFunction];
    if (!validFunction) {
      alert('This function is not available for input building');
      return;
    }
    
    // Check if this is a bundle response and trim 'entry[].resource.' prefix if needed
    const currentFunction = functions.find(f => f.value === selectedFunction);
    let cleanedPath = path;
    
    if (currentFunction && currentFunction.isBundle) {
      // Remove bundle structure prefixes for any bundle response
      if (path.startsWith('entry[].resource.')) {
        cleanedPath = path.replace('entry[].resource.', '');
        console.log(`🧹 Trimmed bundle path from '${path}' to '${cleanedPath}'`);
      } else if (path.startsWith('entry.resource.')) {
        // Handle case where array notation might be missing
        cleanedPath = path.replace('entry.resource.', '');
        console.log(`🧹 Trimmed bundle path from '${path}' to '${cleanedPath}'`);
      } else if (path.includes('.resource.')) {
        // Handle any other resource prefix patterns
        cleanedPath = path.replace(/.*\.resource\./, '');
        console.log(`🧹 Trimmed bundle path from '${path}' to '${cleanedPath}'`);
      }
    }
    
    // Use the stored system value instead of looking it up again
    const systemUrn = foundSystemValue || '';
    console.log(`📋 Using stored system URN: ${systemUrn} for path: ${cleanedPath}`);
    
    // Create a descriptive name that includes the system URN
    let generatedName = `${selectedFunction}_${cleanedPath.replace(/\[\]/g, '').replace(/\./g, '_')}`;
    
    if (systemUrn) {
      // Extract a meaningful part from the system URN for the name
      const systemPart = systemUrn.split('/').pop() || systemUrn.split('.').pop() || 'system';
      generatedName = `${systemPart}_${generatedName}`;
    }
    
    // Ensure the name is unique by adding a timestamp if needed
    const existingInputs = JSON.parse(localStorage.getItem('mlInputs') || '[]');
    const existingNames = existingInputs.map(input => input.name.toLowerCase());
    let finalName = generatedName;
    let counter = 1;
    
    while (existingNames.includes(finalName.toLowerCase())) {
      finalName = `${generatedName}_${counter}`;
      counter++;
    }

    // Create new input
    const newInput = {
      id: Date.now(),
      name: finalName,
      description: `Auto-generated from ${selectedFunction} path: ${cleanedPath}${systemUrn ? ` (System: ${systemUrn})` : ''}`,
      function: validFunction,
      path: cleanedPath, // Use the cleaned path
      type: 'string', // Default type
      systemUrn: systemUrn // Store the specific system URN
    };
    
    // Add category for functions that require it
    if (selectedFunction === 'observations') {
      newInput.category = observationCategory;
      newInput.description += ` (Category: ${observationCategory})`;
    } else if (selectedFunction === 'conditions') {
      newInput.category = conditionCategory;
      newInput.description += ` (Category: ${conditionCategory})`;
    }
    
    // Add to localStorage
    localStorage.setItem('mlInputs', JSON.stringify([...savedInputs, newInput]));
    
    // Update local state
    setInputBuilderPaths(prev => new Set([...prev, cleanedPath])); // Use cleaned path for state too
  };

  const formatJsonWithClickableLines = (json) => {
    // Ensure consistent stringification
    const jsonString = JSON.stringify(json, null, 2);
    const lines = jsonString.split('\n');
    
    // Simple path tracking that handles arrays with generic brackets
    const getPathForLine = (lineIndex) => {
      const pathStack = [];
      const isArrayStack = [];
      
      for (let i = 0; i <= lineIndex; i++) {
        const line = lines[i];
        const indent = (line.match(/^\s*/)[0].length) / 2;
        const content = line.trim();
        
        // Adjust stacks based on indentation level
        while (pathStack.length > indent) {
          pathStack.pop();
          isArrayStack.pop();
        }
        
        // Ensure stacks are the right length
        while (pathStack.length < indent) {
          pathStack.push('');
          isArrayStack.push(false);
        }
        
        // Handle property keys
        const keyMatch = content.match(/^"([^"]+)":\s*/);
        if (keyMatch) {
          pathStack[indent] = keyMatch[1];
          isArrayStack[indent] = false;
          
          // Check if this line contains an array start (property: [)
          if (content.includes('[')) {
            isArrayStack[indent] = true;
          } else {
            // Check if the next non-empty line is an array start
            for (let j = i + 1; j < lines.length; j++) {
              const nextLine = lines[j].trim();
              if (nextLine === '') continue;
              if (nextLine === '[') {
                isArrayStack[indent] = true;
              }
              break;
            }
          }
          continue;
        }
        
        // Handle array start
        if (content === '[') {
          // Array start is handled in the property key section
          continue;
        }
        
        // Handle array end
        if (content === ']') {
          // Array end is handled by the stack adjustment
          continue;
        }
      }
      
      // Build the final path with generic array brackets
      let path = '';
      for (let i = 0; i < pathStack.length; i++) {
        if (pathStack[i]) {
          if (path) path += '.';
          path += pathStack[i];
          if (isArrayStack[i]) {
            path += '[]';
          }
        }
      }
      
      // If we're viewing a bundle entry resource, remove the 'resource.' prefix
      // This happens when viewing details of a bundle entry in a modal
      if (path.startsWith('resource.')) {
        path = path.replace('resource.', '');
      }
      
      return path;
    };

    return (
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-12 bg-gray-50 border-r border-gray-200">
          {lines.map((_, index) => (
            <div key={index} className="text-xs text-gray-500 text-right pr-2 py-0.5">
              {index}
            </div>
          ))}
        </div>
        <div className="pl-12 overflow-x-auto">
          <div className="whitespace-pre font-mono text-sm">
            {lines.map((line, index) => {
              const path = getPathForLine(index);
              const isSelected = selectedJsonPaths.has(path);
              const isInInputBuilder = inputBuilderPaths.has(path);
              
              return (
                <div key={index} className="relative">
                  <div
                    className={`${isSelected ? 'bg-blue-100' : ''} ${isInInputBuilder ? 'bg-red-100' : ''} cursor-pointer hover:bg-gray-100`}
                    onClick={() => handleJsonLineClick(path, index)}
                  >
                    {line}
                  </div>
                  {isSelected && (
                    <div className="absolute left-0 top-0 bottom-0 flex items-center pointer-events-none">
                      <div className="ml-4 flex items-center space-x-2 pointer-events-auto">
                        <span className="text-xs text-gray-500 bg-white px-1 rounded">Path: {path}</span>
                      </div>
                    </div>
                  )}
                  {isSelected && (
                    <div className="absolute right-2 top-0 bottom-0 flex items-center pointer-events-none">
                      <div className="pointer-events-auto">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            addToInputBuilder(path, index);
                          }}
                          className={`text-xs px-1.5 py-0.5 rounded ${
                            isInInputBuilder 
                              ? 'bg-red-500 text-white cursor-not-allowed' 
                              : 'bg-green-500 text-white hover:bg-green-600'
                          }`}
                          disabled={isInInputBuilder}
                        >
                          {isInInputBuilder ? 'Added' : 'Add'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderForm = () => {
    if (selectedFunction === 'patients') {
      return (
        <>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Patient Name
            </label>
            <input
              type="text"
              value={patientName}
              onChange={(e) => setPatientName(e.target.value)}
              placeholder="Enter patient name"
              required
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            />
          </div>

          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Birth Date
            </label>
            <input
              type="date"
              value={birthDate || ''}
              onChange={handleDateChange}
              max={format(new Date(), 'yyyy-MM-dd')}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            />
          </div>

          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Gender
            </label>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            >
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700">
            {selectedFunction === 'practitioner' ? 'Practitioner ID' : 'Patient ID'}
          </label>
          <div className="flex space-x-2">
            <input
              type="text"
              value={patientId}
              onChange={(e) => setPatientId(e.target.value)}
              placeholder={selectedFunction === 'practitioner' ? 'Enter practitioner ID' : 'Enter patient ID'}
              required
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            />
            {!selectedPatient && selectedFunction !== 'practitioner' && (
              <button
                type="button"
                onClick={() => {
                  if (patientId) {
                    handleSelectPatient({ id: patientId });
                  }
                }}
                className="mt-1 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Save ID
              </button>
            )}
          </div>
        </div>

        {selectedFunction === 'medications' && (
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Medication Status
            </label>
            <select
              value={medicationStatus}
              onChange={(e) => setMedicationStatus(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            >
              {medicationStatuses.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedFunction === 'observations' && (
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Category
            </label>
            <select
              value={observationCategory}
              onChange={(e) => setObservationCategory(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            >
              {observationCategories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedFunction === 'conditions' && (
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Category
            </label>
            <select
              value={conditionCategory}
              onChange={(e) => setConditionCategory(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            >
              {conditionCategories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);

    try {
      let url;
      if (selectedFunction === 'patients') {
        const params = new URLSearchParams();
        if (patientName) params.append('name', patientName);
        if (birthDate) params.append('birth_date', birthDate);
        if (gender) params.append('gender', gender);
        
        url = `${API_URL}/api/patients?${params.toString()}`;
      } else {
        if (selectedFunction === 'observations') {
          url = `${API_URL}/api/observations/${patientId}?category=${observationCategory}`;
          
          // Add date parameters if dates are selected
          if (startDate) {
            url += `&date=ge${startDate.toISOString().split('T')[0]}`;
          }
          if (endDate) {
            url += `&date=le${endDate.toISOString().split('T')[0]}`;
          }
        } else if (selectedFunction === 'medications') {
          url = `${API_URL}/api/medications/${patientId}`;
          if (medicationStatus) {
            url += `?status=${medicationStatus}`;
          }
        } else if (selectedFunction === 'conditions') {
          url = `${API_URL}/api/conditions/${patientId}`;
          if (conditionCategory) {
            url += `?category=${conditionCategory}`;
          }
        } else if (selectedFunction === 'questionnaire-responses') {
          url = `${API_URL}/api/questionnaire-responses/${patientId}`;
          // Add query parameters for questionnaire responses if needed
          const params = new URLSearchParams();
          if (params.toString()) {
            url += `?${params.toString()}`;
          }
        } else {
          url = `${API_URL}/api/${selectedFunction}/${patientId}`;
        }
      }

      const response = await axios.get(url);
      setResult(response.data);

      // If this is a practitioner request, fetch their roles
      if (selectedFunction === 'practitioner' && response.data) {
        setLoadingRoles(true);
        try {
          const rolesResponse = await axios.get(`${API_URL}/api/practitioner-roles/${patientId}`);
          if (rolesResponse.data && rolesResponse.data.entry) {
            setPractitionerRoles(rolesResponse.data.entry.map(e => e.resource));
          } else {
            setPractitionerRoles([]);
          }
        } catch (error) {
          console.error('Error fetching practitioner roles:', error);
          setPractitionerRoles([]);
        } finally {
          setLoadingRoles(false);
        }
      }

      // If this is a patient request, fetch their encounters
      if (selectedFunction === 'patient' && response.data) {
        try {
          const encountersResponse = await axios.get(`${API_URL}/api/encounters/${patientId}`);
          if (encountersResponse.data && encountersResponse.data.entry) {
            // Sort encounters by date and take the most recent 3
            const sortedEncounters = encountersResponse.data.entry
              .sort((a, b) => {
                const dateA = new Date(a.resource.period?.start || 0);
                const dateB = new Date(b.resource.period?.start || 0);
                return dateB - dateA;
              })
              .slice(0, 3);
            setPatientEncounters(sortedEncounters);
          }
        } catch (error) {
          console.error('Error fetching patient encounters:', error);
          setPatientEncounters([]);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchReportObservations = async (report) => {
    const observationRefs = report.resource.result || [];
    const obsResults = [];
    
    for (const ref of observationRefs) {
      try {
        // Extract observation ID from the reference (format: "Observation/id")
        const observationId = ref.reference.split('/')[1];
        const response = await axios.get(`${API_URL}/api/observation/${observationId}`);
        obsResults.push(response.data);
      } catch (error) {
        console.error('Error fetching observation:', error);
      }
    }
    
    setReportObservations(obsResults);
  };

  const handleShowDetails = async (observation) => {
    setSelectedObservation(observation);
    setShowModal(true);
    
    // If this is a patient, fetch their encounters
    if (observation.resourceType === 'Patient' || observation.resource?.resourceType === 'Patient') {
      const patientId = observation.id || observation.resource?.id;
      console.log('Fetching encounters for patient:', patientId);
      try {
        const response = await axios.get(`${API_URL}/api/encounters/${patientId}`);
        console.log('Encounters response:', response.data);
        if (response.data && response.data.entry) {
          const sortedEncounters = response.data.entry
            .sort((a, b) => {
              const dateA = new Date(a.resource.period?.start || 0);
              const dateB = new Date(b.resource.period?.start || 0);
              return dateB - dateA;
            })
            .slice(0, 3);
          console.log('Sorted encounters:', sortedEncounters);
          setPatientEncounters(sortedEncounters);
        }
      } catch (error) {
        console.error('Error fetching patient encounters:', error);
        setPatientEncounters([]);
      }
    }
    
    if (selectedFunction === 'diagnostic-reports') {
      fetchReportObservations(observation);
    }
  };

  const handleViewQuestionnaire = async (questionnaireReference) => {
    try {
      setLoading(true);
      setError('');
      
      // Extract questionnaire ID from reference
      let questionnaireId;
      if (questionnaireReference.includes('/')) {
        questionnaireId = questionnaireReference.split('/').pop();
      } else {
        questionnaireId = questionnaireReference;
      }
      
      console.log('Fetching questionnaire:', questionnaireId);
      
      // Fetch the questionnaire
      const response = await axios.get(`${API_URL}/api/questionnaire/${questionnaireId}`);
      
      // Show the questionnaire in the modal
      setSelectedObservation({ resource: response.data, resourceType: 'Questionnaire' });
      setShowModal(true);
      
    } catch (error) {
      console.error('Error fetching questionnaire:', error);
      setError(`Failed to fetch questionnaire: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentDetails = async (documentId) => {
    try {
      const response = await axios.get(`${API_URL}/api/document/${documentId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching document details:', error);
      throw error;
    }
  };

  const handleDownloadAttachment = async (doc, contentIndex = 0) => {
    try {
      // First get the full document details
      const documentId = doc.resource.id;
      const fullDocument = await fetchDocumentDetails(documentId);
      
      // Check if we have the specified attachment
      const attachment = fullDocument?.content?.[contentIndex]?.attachment;
      if (!attachment?.url) {
        setError("No attachment found at specified index");
        return;
      }

      // Extract binary ID from URL (assuming format contains "Binary/[id]")
      const binaryMatch = attachment.url.match(/Binary\/([^/]+)/);
      if (!binaryMatch) {
        setError("Invalid binary URL format");
        return;
      }

      const binaryId = binaryMatch[1];
      
      // Get content type from attachment for Cerner compatibility
      const attachmentContentType = attachment.contentType;
      
      // Fetch binary content with content type parameter for Cerner
      const response = await axios.get(`${API_URL}/api/binary/${binaryId}`, {
        params: {
          contentType: attachmentContentType
        }
      });
      
      // Get the content type and determine file extension
      const contentType = response.data.contentType;
      let data = response.data.data;
      let extension;
      
      // Determine file extension based on content type
      if (contentType.includes('text/html')) {
        extension = 'html';
      } else if (contentType.includes('text/rtf')) {
        extension = 'rtf';
      } else if (contentType.includes('application/pdf')) {
        extension = 'pdf';
      } else if (contentType.includes('image/jpeg')) {
        extension = 'jpg';
      } else if (contentType.includes('image/png')) {
        extension = 'png';
      } else if (contentType.includes('image/gif')) {
        extension = 'gif';
      } else if (contentType.includes('text/plain')) {
        extension = 'txt';
      } else {
        extension = contentType.split('/')[1] || 'bin';
      }
      
      // Get filename from attachment or generate one
      const fileName = attachment.title || attachment.filename || `document_${contentIndex + 1}.${extension}`;
      
      // Handle different data formats
      let blob;
      if (typeof data === 'string') {
        // For text-based content (HTML, RTF, etc.)
        blob = new Blob([data], { type: contentType });
      } else if (data && typeof data === 'object' && data.raw) {
        // For binary content with raw array data (new format)
        const uint8Array = new Uint8Array(data.raw);
        blob = new Blob([uint8Array], { type: contentType });
      } else if (typeof data === 'string') {
        // For binary content with base64 string (legacy format)
        const binData = atob(data);
        blob = new Blob([binData], { type: contentType });
      } else {
        // Fallback for unknown data format
        console.warn('Unknown data format:', typeof data, data);
        blob = new Blob([data], { type: contentType });
      }
      
      // Create a download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      setError(error.message);
    }
  };

  // Keep the old function for backward compatibility
  const handleDownloadBinary = async (doc) => {
    await handleDownloadAttachment(doc, 0);
  };

  const handleViewMedication = async (medicationReference) => {
    try {
      setLoadingMedication(true);
      // Extract medication ID from reference (format: "Medication/[id]")
      const medicationId = medicationReference.split('/')[1];
      const response = await axios.get(`${API_URL}/api/medication/${medicationId}`);
      setMedicationDetails(response.data);
    } catch (error) {
      setError(error.response?.data?.error || error.message);
    } finally {
      setLoadingMedication(false);
    }
  };

  // Add this new component for formatted patient display
  const FormattedPatientInfo = ({ patient, encounters }) => {
    const formatAddress = (address) => {
      if (!address || !address[0]) return 'No address provided';
      const addr = address[0];
      return [
        addr.line?.join(', '),
        addr.city,
        addr.state,
        addr.postalCode,
        addr.country
      ].filter(Boolean).join(', ');
    };

    const formatContact = (telecom) => {
      if (!telecom || !telecom.length) return 'No contact information';
      return telecom.map(t => `${t.system}: ${t.value} (${t.use})`).join(', ');
    };

    const formatName = (name) => {
      if (!name || !name[0]) return 'No name provided';
      const n = name[0];
      return `${n.prefix ? n.prefix.join(' ') + ' ' : ''}${n.given ? n.given.join(' ') : ''} ${n.family || ''}`;
    };

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
        {/* Demographics Card */}
        <div className="bg-white rounded-lg shadow p-6 w-full">
          <h6 className="text-primary font-medium mb-4">Demographics</h6>
          <div className="space-y-3">
            <div className="flex justify-between items-start">
              <span className="text-sm text-gray-500 min-w-[100px]">Name:</span>
              <span className="text-sm text-gray-900 text-right flex-1 ml-4">{formatName(patient.name)}</span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-sm text-gray-500 min-w-[100px]">Gender:</span>
              <span className="text-sm text-gray-900 text-right flex-1 ml-4">
                {patient.gender?.charAt(0).toUpperCase() + patient.gender?.slice(1) || 'Not specified'}
              </span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-sm text-gray-500 min-w-[100px]">Birth Date:</span>
              <span className="text-sm text-gray-900 text-right flex-1 ml-4">
                {patient.birthDate ? new Date(patient.birthDate).toLocaleDateString() : 'Not specified'}
              </span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-sm text-gray-500 min-w-[100px]">ID:</span>
              <span className="text-sm text-gray-900 text-right flex-1 ml-4">{patient.id}</span>
            </div>
          </div>
        </div>

        {/* Contact Information Card */}
        <div className="bg-white rounded-lg shadow p-6 w-full">
          <h6 className="text-primary font-medium mb-4">Contact Information</h6>
          <div className="space-y-3">
            <div className="flex justify-between items-start">
              <span className="text-sm text-gray-500 min-w-[100px]">Address:</span>
              <span className="text-sm text-gray-900 text-right flex-1 ml-4">{formatAddress(patient.address)}</span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-sm text-gray-500 min-w-[100px]">Contact:</span>
              <span className="text-sm text-gray-900 text-right flex-1 ml-4">{formatContact(patient.telecom)}</span>
            </div>
          </div>
        </div>

        {/* Additional Information Card */}
        {(patient.maritalStatus || (patient.communication && patient.communication.length > 0)) && (
          <div className="bg-white rounded-lg shadow p-6 w-full">
            <h6 className="text-primary font-medium mb-4">Additional Information</h6>
            <div className="space-y-3">
              {patient.maritalStatus && (
                <div className="flex justify-between items-start">
                  <span className="text-sm text-gray-500 min-w-[100px]">Marital Status:</span>
                  <span className="text-sm text-gray-900 text-right flex-1 ml-4">
                    {patient.maritalStatus.text || patient.maritalStatus.coding?.[0]?.display || 'Not specified'}
                  </span>
                </div>
              )}
              {patient.communication && patient.communication.length > 0 && (
                <div className="flex justify-between items-start">
                  <span className="text-sm text-gray-500 min-w-[100px]">Language:</span>
                  <span className="text-sm text-gray-900 text-right flex-1 ml-4">
                    {patient.communication.map(comm => 
                      comm.language?.coding?.[0]?.display || 'Not specified'
                    ).join(', ')}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recent Encounters Card */}
        {encounters && encounters.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 w-full lg:col-span-3">
            <h6 className="text-primary font-medium mb-4">Recent Encounters</h6>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Start Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">Provider</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {encounters.map((encounter, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {encounter.resource.type?.[0]?.text || 
                         encounter.resource.type?.[0]?.coding?.[0]?.display || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {encounter.resource.status?.charAt(0).toUpperCase() + 
                         encounter.resource.status?.slice(1) || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {encounter.resource.period?.start ? 
                         new Date(encounter.resource.period.start).toLocaleString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {encounter.resource.participant?.[0]?.individual?.display || 
                         encounter.resource.serviceProvider?.display || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Add this new component for formatting diagnostic report details
  const DiagnosticReportDetails = ({ report }) => {
    const [observations, setObservations] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchObservations = async () => {
        const observationRefs = report.resource.result || [];
        const obsResults = [];
        
        for (const ref of observationRefs) {
          try {
            // Extract observation ID from the reference (format: "Observation/id")
            const observationId = ref.reference.split('/')[1];
            const response = await axios.get(`${API_URL}/api/observation/${observationId}`);
            obsResults.push(response.data);
          } catch (error) {
            console.error('Error fetching observation:', error);
          }
        }
        
        setObservations(obsResults);
        setLoading(false);
      };

      fetchObservations();
    }, [report]);

    const getResultStyle = (resource) => {
      if (!resource.referenceRange?.[0]) return {};

      const value = resource.valueQuantity?.value;
      if (!value) return {};

      const low = resource.referenceRange[0].low?.value;
      const high = resource.referenceRange[0].high?.value;

      if ((low !== undefined && value < low) || (high !== undefined && value > high)) {
        return { color: '#dc3545', fontWeight: 'bold' }; // Red for out of range
      }
      return { color: '#198754', fontWeight: 'bold' }; // Green for within range
    };

    const formatReferenceRange = (resource) => {
      if (!resource.referenceRange?.[0]) return 'N/A';
      const range = resource.referenceRange[0];
      
      if (range.text) return range.text;
      
      if (range.low?.value !== undefined && range.high?.value !== undefined) {
        return `${range.low.value} - ${range.high.value} ${range.low.unit || ''}`;
      }
      
      if (range.low?.value !== undefined) {
        return `> ${range.low.value} ${range.low.unit || ''}`;
      }
      
      if (range.high?.value !== undefined) {
        return `< ${range.high.value} ${range.high.unit || ''}`;
      }

      return 'N/A';
    };

    if (loading) {
      return (
        <div className="text-center p-4">
          <span className="spinner-border text-primary" role="status" />
          <p className="mt-2">Loading observations...</p>
        </div>
      );
    }

    return (
      <div className="diagnostic-report-details">
        <div className="mb-4">
          <h6 className="text-primary">Report Information</h6>
                      <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <tbody>
                  <tr>
                    <th width="200" className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Report Type
                    </th>
                    <td className="px-2 sm:px-6 py-4 text-sm text-gray-900">
                      <div className="truncate max-w-[200px] sm:max-w-none" title={report.resource.code?.text || report.resource.code?.coding?.[0]?.display || 'N/A'}>
                        {report.resource.code?.text || report.resource.code?.coding?.[0]?.display || 'N/A'}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <td className="px-2 sm:px-6 py-4 text-sm text-gray-900">
                      {report.resource.status?.charAt(0).toUpperCase() + report.resource.status?.slice(1) || 'N/A'}
                    </td>
                  </tr>
                  <tr>
                    <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Issued Date
                    </th>
                    <td className="px-2 sm:px-6 py-4 text-sm text-gray-900">
                      {report.resource.issued ? new Date(report.resource.issued).toLocaleString() : 'N/A'}
                    </td>
                  </tr>
                  <tr>
                    <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Performer
                    </th>
                    <td className="px-2 sm:px-6 py-4 text-sm text-gray-900">
                      <div className="truncate max-w-[200px] sm:max-w-none" title={report.resource.performer?.[0]?.display || report.resource.performer?.[0]?.reference || 'N/A'}>
                        {report.resource.performer?.[0]?.display || report.resource.performer?.[0]?.reference || 'N/A'}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
        </div>

        {observations.length > 0 && (
          <div className="mb-4">
            <h6 className="text-primary">Results ({observations.length})</h6>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Test
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reference Range
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Interpretation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {observations.map((obs, index) => {
                  const resultStyle = getResultStyle(obs);
                  
                  return (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {obs.code?.text || obs.code?.coding?.[0]?.display || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span style={resultStyle}>
                          {obs.valueQuantity?.value || 
                           obs.valueString || 
                           obs.valueCodeableConcept?.text ||
                           (obs.component ? 
                             obs.component.map(comp => 
                               `${comp.code?.coding?.[0]?.display || ''}: ${comp.valueQuantity?.value || ''}`
                             ).join(', ') 
                             : 'N/A')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatReferenceRange(obs)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {obs.valueQuantity?.unit || ''}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span style={resultStyle}>
                          {obs.interpretation?.[0]?.text || 
                           obs.interpretation?.[0]?.coding?.[0]?.display || ''}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {obs.effectiveDateTime ? new Date(obs.effectiveDateTime).toLocaleString() : 'N/A'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };


  const renderError = (error) => {
    if (!error) return null;

    // Handle FHIR API errors
    if (error.details?.fhirError) {
      return (
        <div className="mt-4 rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">FHIR API Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p className="font-medium">{error.details.message}</p>
                <p className="mt-1">Status: {error.details.status} {error.details.statusText}</p>
                {error.details.fhirError.issue && (
                  <div className="mt-2">
                    <p className="font-medium">Issues:</p>
                    <ul className="list-disc pl-5 mt-1">
                      {error.details.fhirError.issue.map((issue, index) => (
                        <li key={index}>
                          {issue.details?.text || issue.diagnostics || 'Unknown issue'}
                          {issue.severity && ` (${issue.severity})`}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Handle other errors
    return (
      <div className="mt-4 rounded-md bg-red-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">
              {error.details?.message || error.message || 'An unknown error occurred'}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Update the error display in the form
  return (
    <div className="bg-gray-50">
              <div className="w-[85%] mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              EPIC FHIR API Client
            </h3>
            {selectedPatient ? (
              <SelectedPatient 
                patient={selectedPatient} 
                onClear={handleClearPatient}
                onSelect={handleSelectPatient}
              />
            ) : (
              <div className="flex items-center space-x-2 bg-primary-50 px-3 py-2 rounded-md">
                <span className="text-sm text-primary-700">Select Patient:</span>
                <select
                  className="text-sm px-2 py-1 rounded border border-primary-200 focus:outline-none"
                  onChange={e => {
                    const patient = savedPatientsData.patients.find(p => p.id === e.target.value);
                    if (patient) handleSelectPatient(patient);
                  }}
                  defaultValue=""
                >
                  <option value="" disabled>Select...</option>
                  {savedPatientsData.patients.map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          <div className="px-4 py-5 sm:p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Function
                </label>
                <select
                  value={selectedFunction}
                  onChange={handleFunctionChange}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                >
                  <option value="patients">Search Patients</option>
                  {functions.map(func => {
                    const Icon = func.icon;
                    return (
                      <option key={func.value} value={func.value} className="flex items-center">
                        <Icon className="h-5 w-5 mr-2" />
                        {func.label}
                      </option>
                    );
                  })}
                </select>
              </div>

              {renderForm()}

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                    loading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    'Run Function'
                  )}
                </button>
              </div>
            </form>

            {error && renderError(error)}

            {/* Results section */}
            {result && (
              <div className="mt-8">
                <div className="overflow-x-auto">
                  {selectedFunction === 'patient' && (
                    <>
                      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                          <h3 className="text-lg leading-6 font-medium text-gray-900">Patient Information</h3>
                          <button
                            onClick={() => handleShowDetails(result)}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                          >
                            View Details
                          </button>
                        </div>
                        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
                            <FormattedPatientInfo patient={result} encounters={patientEncounters} />
                        </div>
                      </div>
                    </>
                  )}

                  {selectedFunction === 'practitioner' && (
                    <>
                      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                          <h3 className="text-lg leading-6 font-medium text-gray-900">Practitioner Information</h3>
                          <button
                            onClick={() => handleShowDetails(result)}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                          >
                            View Details
                          </button>
                        </div>
                        <div className="border-t border-gray-200">
                          <dl className="sm:divide-y sm:divide-gray-200">
                            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                              <dt className="text-sm font-medium text-gray-500">Name</dt>
                              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {result.name?.[0]?.text || 
                                 `${result.name?.[0]?.given?.join(' ') || ''} ${result.name?.[0]?.family || ''}`.trim() || 
                                 'N/A'}
                              </dd>
                            </div>
                            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                              <dt className="text-sm font-medium text-gray-500">ID</dt>
                              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{result.id || 'N/A'}</dd>
                            </div>
                            {result.identifier && result.identifier.length > 0 && (
                              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">Identifiers</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                  {result.identifier.map((id, idx) => (
                                    <div key={idx} className="mb-1">
                                      <span className="font-medium">{id.type?.text || id.system || 'ID'}:</span> {id.value}
                                    </div>
                                  ))}
                                </dd>
                              </div>
                            )}
                            {result.qualification && result.qualification.length > 0 && (
                              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">Qualifications</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                  {result.qualification.map((qual, idx) => (
                                    <div key={idx} className="mb-1">
                                      {qual.code?.text || qual.code?.coding?.[0]?.display || 'N/A'}
                                    </div>
                                  ))}
                                </dd>
                              </div>
                            )}
                            {result.telecom && result.telecom.length > 0 && (
                              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">Contact</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                  {result.telecom.map((contact, idx) => (
                                    <div key={idx} className="mb-1">
                                      <span className="font-medium capitalize">{contact.system}:</span> {contact.value}
                                    </div>
                                  ))}
                                </dd>
                              </div>
                            )}
                            {result.address && result.address.length > 0 && (
                              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">Address</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                  {result.address.map((addr, idx) => (
                                    <div key={idx} className="mb-1">
                                      {addr.line?.join(', ')}{addr.line ? ', ' : ''}
                                      {addr.city}{addr.city ? ', ' : ''}{addr.state} {addr.postalCode}
                                    </div>
                                  ))}
                                </dd>
                              </div>
                            )}
                          </dl>
                        </div>
                      </div>

                      {/* PractitionerRole Section */}
                      {loadingRoles && (
                        <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg p-6 text-center">
                          <svg className="animate-spin h-6 w-6 text-primary-600 mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                          </svg>
                          <p className="text-sm text-gray-500">Loading Practitioner Roles...</p>
                        </div>
                      )}

                      {!loadingRoles && practitionerRoles.length > 0 && (
                        <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
                          <div className="px-4 py-5 sm:px-6">
                            <h3 className="text-lg leading-6 font-medium text-gray-900">Practitioner Roles ({practitionerRoles.length})</h3>
                            <p className="mt-1 max-w-2xl text-sm text-gray-500">Roles, specialties, and locations associated with this practitioner</p>
                          </div>
                          <div className="border-t border-gray-200">
                            {practitionerRoles.map((role, roleIdx) => (
                              <div key={roleIdx} className={`${roleIdx > 0 ? 'border-t border-gray-200' : ''}`}>
                                <div className="px-4 py-4 sm:px-6 flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        Role {roleIdx + 1}
                                      </span>
                                      {role.active !== undefined && (
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${role.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                          {role.active ? 'Active' : 'Inactive'}
                                        </span>
                                      )}
                                    </div>
                                    <dl className="sm:divide-y sm:divide-gray-200">
                                      {role.code && role.code.length > 0 && (
                                        <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
                                          <dt className="text-sm font-medium text-gray-500">Specialty / Role</dt>
                                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                            {role.code.map((c, i) => (
                                              <div key={i}>{c.coding?.[0]?.display || c.text || 'N/A'}</div>
                                            ))}
                                          </dd>
                                        </div>
                                      )}
                                      {role.specialty && role.specialty.length > 0 && (
                                        <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
                                          <dt className="text-sm font-medium text-gray-500">Specialty</dt>
                                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                            {role.specialty.map((s, i) => (
                                              <div key={i}>{s.coding?.[0]?.display || s.text || 'N/A'}</div>
                                            ))}
                                          </dd>
                                        </div>
                                      )}
                                      {role.organization && (
                                        <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
                                          <dt className="text-sm font-medium text-gray-500">Organization</dt>
                                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                            {role.organization.display || role.organization.reference || 'N/A'}
                                          </dd>
                                        </div>
                                      )}
                                      {role.location && role.location.length > 0 && (
                                        <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
                                          <dt className="text-sm font-medium text-gray-500">Location(s)</dt>
                                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                            {role.location.map((loc, i) => (
                                              <div key={i}>{loc.display || loc.reference || 'N/A'}</div>
                                            ))}
                                          </dd>
                                        </div>
                                      )}
                                      {role.telecom && role.telecom.length > 0 && (
                                        <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
                                          <dt className="text-sm font-medium text-gray-500">Contact</dt>
                                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                            {role.telecom.map((contact, i) => (
                                              <div key={i}>
                                                <span className="font-medium capitalize">{contact.system}:</span> {contact.value}
                                              </div>
                                            ))}
                                          </dd>
                                        </div>
                                      )}
                                      {role.availabilityExceptions && (
                                        <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
                                          <dt className="text-sm font-medium text-gray-500">Availability Notes</dt>
                                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                            {role.availabilityExceptions}
                                          </dd>
                                        </div>
                                      )}
                                      {role.period && (
                                        <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
                                          <dt className="text-sm font-medium text-gray-500">Period</dt>
                                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                            {role.period.start && <span>From: {new Date(role.period.start).toLocaleDateString()}</span>}
                                            {role.period.end && <span> To: {new Date(role.period.end).toLocaleDateString()}</span>}
                                            {!role.period.start && !role.period.end && 'N/A'}
                                          </dd>
                                        </div>
                                      )}
                                    </dl>
                                  </div>
                                  <button
                                    onClick={() => handleShowDetails(role)}
                                    className="ml-4 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 flex-shrink-0"
                                  >
                                    View Details
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {!loadingRoles && practitionerRoles.length === 0 && result && (
                        <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg p-6 text-center">
                          <p className="text-sm text-gray-500">No Practitioner Roles found for this practitioner.</p>
                        </div>
                      )}
                    </>
                  )}

                  {selectedFunction === 'observations' && result.entry && (
                    <>
                      <div className="flex justify-between items-center mb-4">
                        <h5 className="text-lg font-medium text-gray-900">Observations</h5>
                      </div>
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Test Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Result</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date/Time</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference Range</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {result.entry.map((obs, index) => {
                            const resource = obs.resource;
                            return (
                              <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {resource.code?.text || resource.code?.coding?.[0]?.display || 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {resource.valueQuantity?.value || resource.valueString || 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {resource.valueQuantity?.unit || ''}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {resource.effectiveDateTime ? new Date(resource.effectiveDateTime).toLocaleString() : 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {resource.referenceRange?.[0]?.text || 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  <div className="flex space-x-2">
                                    <button
                                      className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-primary-700 bg-primary-100 hover:bg-primary-200"
                                      onClick={() => handleShowDetails(obs)}
                                    >
                                      View Details
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </>
                  )}

                  {selectedFunction === 'documents' && result.entry && (
                    <>
                      <div className="flex justify-between items-center mb-4">
                        <h5 className="text-lg font-medium text-gray-900">Documents</h5>
                      </div>
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Attachments</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {result.entry.map((doc, index) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {doc.resource.type?.text || doc.resource.type?.coding?.[0]?.display || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {doc.resource.date ? new Date(doc.resource.date).toLocaleString() : 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {doc.resource.status?.charAt(0).toUpperCase() + doc.resource.status?.slice(1) || 'N/A'}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900">
                                <div className="space-y-1">
                                  {doc.resource.content && doc.resource.content.length > 0 ? (
                                    doc.resource.content.map((content, contentIndex) => {
                                      const attachment = content.attachment;
                                      const contentType = attachment?.contentType || 'Unknown';
                                      const fileName = attachment?.title || `Attachment ${contentIndex + 1}`;
                                      const fileSize = attachment?.size ? `${Math.round(attachment.size / 1024)} KB` : '';
                                      
                                      return (
                                        <div key={contentIndex} className="flex items-center justify-between bg-gray-50 px-2 py-1 rounded text-xs">
                                          <div className="flex-1 min-w-0">
                                            <div className="font-medium truncate">{fileName}</div>
                                            <div className="text-gray-500">{contentType} {fileSize && `(${fileSize})`}</div>
                                          </div>
                                          {attachment?.url && (
                                            <button
                                              className="ml-2 inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-primary-700 bg-primary-100 hover:bg-primary-200"
                                              onClick={() => handleDownloadAttachment(doc, contentIndex)}
                                            >
                                              Download
                                            </button>
                                          )}
                                        </div>
                                      );
                                    })
                                  ) : (
                                    <span className="text-gray-500">No attachments</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <button
                                  className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700"
                                  onClick={() => handleShowDetails(doc)}
                                >
                                  View Details
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}

                  {selectedFunction === 'medications' && result.entry && (
                    <>
                      <div className="flex justify-between items-center mb-4">
                        <h5 className="text-lg font-medium text-gray-900">Medications</h5>
                      </div>
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Medication</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">Dosage</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {result.entry.map((med, index) => (
                            <tr key={index}>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <div className="break-words">
                                  {med.resource.medicationCodeableConcept?.text || 
                                   med.resource.medicationCodeableConcept?.coding?.[0]?.display || 'N/A'}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <div className="break-words">
                                  {med.resource.status?.charAt(0).toUpperCase() + med.resource.status?.slice(1) || 'N/A'}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <div className="break-words">
                                  {med.resource.dosageInstruction?.[0]?.text || 'N/A'}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <div className="break-words">
                                  {med.resource.authoredOn ? new Date(med.resource.authoredOn).toLocaleString() : 'N/A'}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <button
                                  className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700"
                                  onClick={() => handleShowDetails(med)}
                                >
                                  View Details
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}

                  {selectedFunction === 'diagnostic-reports' && result.entry && (
                    <>
                      <h5 className="text-lg font-medium text-gray-900 mb-4">Diagnostic Reports</h5>
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {result.entry.map((report, index) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {report.resource.code?.text || report.resource.code?.coding?.[0]?.display || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {report.resource.status?.charAt(0).toUpperCase() + report.resource.status?.slice(1) || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {report.resource.issued ? new Date(report.resource.issued).toLocaleString() : 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <button
                                  className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                  onClick={() => handleShowDetails(report)}
                                >
                                  View Details
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}

                  {selectedFunction === 'encounters' && result.entry && (
                    <>
                      <div className="flex justify-between items-center mb-4">
                        <h5 className="text-lg font-medium text-gray-900">Patient Encounters</h5>
                      </div>
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {result.entry.map((encounter, index) => (
                            <tr key={index}>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <div className="break-words">
                                  {encounter.resource.type?.[0]?.text || 
                                   encounter.resource.type?.[0]?.coding?.[0]?.display || 'N/A'}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <div className="break-words">
                                  {encounter.resource.status?.charAt(0).toUpperCase() + 
                                   encounter.resource.status?.slice(1) || 'N/A'}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <div className="break-words">
                                  {encounter.resource.period?.start ? 
                                   new Date(encounter.resource.period.start).toLocaleString() : 'N/A'}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <div className="break-words">
                                  {encounter.resource.period?.end ? 
                                   new Date(encounter.resource.period.end).toLocaleString() : 'N/A'}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <div className="break-words">
                                  {encounter.resource.participant?.[0]?.individual?.display || 
                                   encounter.resource.serviceProvider?.display || 'N/A'}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <button
                                  className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700"
                                  onClick={() => handleShowDetails(encounter)}
                                >
                                  View Details
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}

                  {selectedFunction === 'patients' && result.entry && (
                    <>
                      <div className="flex justify-between items-center mb-4">
                        <h5 className="text-lg font-medium text-gray-900">Search Results</h5>
                      </div>
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">ID</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Name</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Gender</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Birth Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Address</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {result.entry.map((entry, index) => {
                            const patient = entry.resource;
                            const patientName = patient.name?.[0]?.given?.join(' ') + ' ' + patient.name?.[0]?.family;
                            return (
                              <tr key={index}>
                                <td className="px-4 py-4 text-sm text-gray-900">
                                  <div className="break-words">
                                    {patientName}
                                  </div>
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-900">
                                  <div className="flex items-center space-x-2">
                                    <span className="font-mono">{patient.id}</span>
                                    <button
                                      onClick={() => handleCopyId(patient.id)}
                                      className="inline-flex items-center p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                                      title="Copy ID"
                                    >
                                      <ClipboardDocumentIcon className="h-4 w-4" />
                                    </button>
                                    {copiedId === patient.id && (
                                      <span className="text-xs text-green-600">Copied!</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-900">
                                  <div className="break-words">
                                    {patient.gender?.charAt(0).toUpperCase() + patient.gender?.slice(1) || 'N/A'}
                                  </div>
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-900">
                                  <div className="break-words">
                                    {patient.birthDate ? new Date(patient.birthDate).toLocaleDateString() : 'N/A'}
                                  </div>
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-900">
                                  <div className="break-words">
                                    {patient.address?.[0] ? (
                                      <>
                                        {patient.address[0].line?.join(', ')}<br />
                                        {patient.address[0].city}, {patient.address[0].state} {patient.address[0].postalCode}
                                      </>
                                    ) : 'N/A'}
                                  </div>
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-900">
                                  <div className="flex space-x-2">
                                    <button
                                      className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700"
                                      onClick={() => handleShowDetails(entry)}
                                    >
                                      View Details
                                    </button>
                                    <button
                                      className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-primary-700 bg-primary-100 hover:bg-primary-200"
                                      onClick={() => handleSelectPatient(patient)}
                                    >
                                      Select Patient
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </>
                  )}

                  {selectedFunction === 'immunizations' && result.entry && (
                    <>
                      <div className="flex justify-between items-center mb-4">
                        <h5 className="text-lg font-medium text-gray-900">Patient Immunizations</h5>
                      </div>
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vaccine</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {result.entry.map((immunization, index) => (
                            <tr key={index}>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                {immunization.resource.vaccineCode?.text || 
                                 immunization.resource.vaccineCode?.coding?.[0]?.display || 'N/A'}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                {immunization.resource.status?.charAt(0).toUpperCase() + 
                                 immunization.resource.status?.slice(1) || 'N/A'}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                {immunization.resource.occurrenceDateTime ? 
                                 new Date(immunization.resource.occurrenceDateTime).toLocaleDateString() : 'N/A'}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <button
                                  className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700"
                                  onClick={() => handleShowDetails(immunization)}
                                >
                                  View Details
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}

                  {selectedFunction === 'conditions' && result.entry && (
                    <>
                      <div className="flex justify-between items-center mb-4">
                        <h5 className="text-lg font-medium text-gray-900">Patient Conditions</h5>
                      </div>
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Condition</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clinical Status</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verification Status</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Onset Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recorded Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {result.entry.map((condition, index) => (
                            <tr key={index}>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <div className="break-words">
                                  {condition.resource.code?.text || 
                                   condition.resource.code?.coding?.[0]?.display || 'N/A'}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <div className="break-words">
                                  {condition.resource.clinicalStatus?.coding?.[0]?.display || 'N/A'}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <div className="break-words">
                                  {condition.resource.verificationStatus?.coding?.[0]?.display || 'N/A'}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <div className="break-words">
                                  {condition.resource.onsetDateTime ? 
                                   new Date(condition.resource.onsetDateTime).toLocaleDateString() : 'N/A'}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <div className="break-words">
                                  {condition.resource.recordedDate ? 
                                   new Date(condition.resource.recordedDate).toLocaleDateString() : 'N/A'}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <button
                                  className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700"
                                  onClick={() => handleShowDetails(condition)}
                                >
                                  View Details
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}

                  {selectedFunction === 'questionnaire-responses' && result.entry && (
                    <>
                      <div className="flex justify-between items-center mb-4">
                        <h5 className="text-lg font-medium text-gray-900">Patient Questionnaire Responses</h5>
                      </div>
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Questionnaire</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Authored</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Author</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {result.entry.map((response, index) => (
                            <tr key={index}>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <div className="break-words">
                                  {response.resource.questionnaire ? 
                                   (response.resource.questionnaire.includes('/') ? 
                                    response.resource.questionnaire.split('/').pop() : 
                                    response.resource.questionnaire) : 'N/A'}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <div className="break-words">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    response.resource.status === 'completed' ? 'bg-green-100 text-green-800' :
                                    response.resource.status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' :
                                    response.resource.status === 'amended' ? 'bg-blue-100 text-blue-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {response.resource.status || 'N/A'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <div className="break-words">
                                  {response.resource.authored ? 
                                   new Date(response.resource.authored).toLocaleDateString() : 'N/A'}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <div className="break-words">
                                  {response.resource.author?.display || 
                                   response.resource.author?.reference || 'N/A'}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <div className="space-x-2">
                                  <button
                                    className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700"
                                    onClick={() => handleShowDetails(response)}
                                  >
                                    View Response
                                  </button>
                                  {response.resource.questionnaire && (
                                    <button
                                      className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                                      onClick={() => handleViewQuestionnaire(response.resource.questionnaire)}
                                    >
                                      View Questionnaire
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div>
                  <div className="w-full">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                        Resource Details
                      </h3>
                      <button
                        type="button"
                        className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        onClick={() => {
                          setShowModal(false);
                          setMedicationDetails(null);
                          setReportObservations([]);
                          setSelectedJsonPaths(new Set());
                        }}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>

                    {/* Add instructions section */}
                    <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="text-sm font-medium text-blue-800 mb-2">How to use JSON Path Selection:</h4>
                      <ul className="text-sm text-blue-700 space-y-2">
                        <li className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>Click any line in the JSON to select it and see its path</span>
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>Click the line again to deselect it</span>
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>Click add to use it as a field in the Input Builder</span>
                        </li>
                      </ul>
                    </div>

                    <div className="mt-4">
                      {selectedFunction === 'diagnostic-reports' && reportObservations.length > 0 && (
                        <div className="mb-6">
                          <h4 className="text-lg font-medium text-gray-900 mb-4">Report Results</h4>
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Test</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Result</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference Range</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {reportObservations.map((obs, index) => {
                                const isAbnormal = obs.interpretation?.[0]?.coding?.[0]?.code === 'A';
                                return (
                                  <tr key={index}>
                                    <td className="px-4 py-4 text-sm text-gray-900">
                                      {obs.code?.text || obs.code?.coding?.[0]?.display || 'N/A'}
                                    </td>
                                    <td className={`px-4 py-4 text-sm ${isAbnormal ? 'text-red-600 font-semibold' : 'text-gray-900'}`}>
                                      {obs.valueQuantity?.value || obs.valueString || 'N/A'}
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-900">
                                      {obs.valueQuantity?.unit || 'N/A'}
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-900">
                                      {obs.referenceRange?.[0]?.text || 
                                       (obs.referenceRange?.[0]?.low?.value !== undefined && obs.referenceRange?.[0]?.high?.value !== undefined
                                         ? `${obs.referenceRange[0].low.value} - ${obs.referenceRange[0].high.value}`
                                         : 'N/A')}
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-900">
                                      {obs.status?.charAt(0).toUpperCase() + obs.status?.slice(1) || 'N/A'}
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-900">
                                      {obs.effectiveDateTime ? new Date(obs.effectiveDateTime).toLocaleString() : 'N/A'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 overflow-auto max-h-[70vh]">
                        <div className="mb-2 text-sm text-gray-600">
                          Click any line to see its JSON path
                        </div>
                        <div className="font-mono text-sm">
                          {formatJsonWithClickableLines(selectedObservation)}
                        </div>
                      </div>

                      {selectedFunction === 'medications' && 
                       selectedObservation?.resource?.medicationReference?.reference && (
                        <div className="mt-6">
                          <button
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            onClick={() => handleViewMedication(selectedObservation.resource.medicationReference.reference)}
                            disabled={loadingMedication}
                          >
                            {loadingMedication ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Loading Medication...
                              </>
                            ) : 'View Medication Details'}
                          </button>

                          {medicationDetails && (
                            <div className="mt-4">
                              <h4 className="text-lg font-medium text-gray-900 mb-2">Medication Details</h4>
                              {medicationDetails.drugInfo && (
                                <div className="bg-gray-50 rounded-md p-4 mb-4">
                                  <h5 className="text-sm font-medium text-gray-900 mb-2">Drug Information</h5>
                                  <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                                    <div>
                                      <dt className="text-sm font-medium text-gray-500">Name</dt>
                                      <dd className="mt-1 text-sm text-gray-900">{medicationDetails.drugInfo.name}</dd>
                                    </div>
                                    <div>
                                      <dt className="text-sm font-medium text-gray-500">RxNorm Codes</dt>
                                      <dd className="mt-1 text-sm text-gray-900">
                                        {medicationDetails.drugInfo.rxnormCodes.join(', ')}
                                      </dd>
                                    </div>
                                    <div className="sm:col-span-2">
                                      <dt className="text-sm font-medium text-gray-500">Therapeutic Classes</dt>
                                      <dd className="mt-1 text-sm text-gray-900">
                                        {medicationDetails.drugInfo.therapeuticClasses.join(', ')}
                                      </dd>
                                    </div>
                                    <div className="sm:col-span-2">
                                      <dt className="text-sm font-medium text-gray-500">Forms</dt>
                                      <dd className="mt-1 text-sm text-gray-900">
                                        {medicationDetails.drugInfo.forms.join(', ')}
                                      </dd>
                                    </div>
                                  </dl>
                                </div>
                              )}
                              <pre className="bg-gray-50 rounded p-4 overflow-auto max-h-[30vh] text-left">
                                <code className="text-sm text-left font-mono">
                                  {JSON.stringify(medicationDetails.medication, null, 2)}
                                </code>
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    setShowModal(false);
                    setMedicationDetails(null);
                    setReportObservations([]);
                    setSelectedJsonPaths(new Set()); // Clear selected paths when closing
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}

function Navigation() {
  const location = useLocation();
  const [currentSystem, setCurrentSystem] = useState('epic');
  const [isSwitching, setIsSwitching] = useState(false);
  
  // Get current system on mount
  useEffect(() => {
    const getCurrentSystem = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/system/current`);
        setCurrentSystem(response.data.system);
      } catch (error) {
        console.error('Error getting current system:', error);
      }
    };
    getCurrentSystem();
  }, []);

  const handleSystemSwitch = async (newSystem) => {
    if (newSystem === currentSystem) return;
    
    setIsSwitching(true);
    try {
      const response = await axios.post(`${API_URL}/api/system/switch`, {
        system: newSystem
      });
      setCurrentSystem(response.data.system);
      // Show success message
      alert(`Switched to ${newSystem} system successfully!`);
    } catch (error) {
      console.error('Error switching system:', error);
      alert(`Error switching to ${newSystem}: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsSwitching(false);
    }
  };
  
  return (
    <nav className="bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <img
                src="/C_only.png"
                alt="Charlotte Logo"
                className="h-12 w-auto mr-2"
              />
              <span className="text-xl font-bold text-gray-900">AI4Health ML Studio</span>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              <Link
                to="/"
                className={`border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                  location.pathname === '/' ? 'border-primary-500 text-primary-600' : ''
                }`}
              >
                FHIR Client
              </Link>
              <Link
                to="/bulk-data"
                className={`border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                  location.pathname === '/bulk-data' ? 'border-primary-500 text-primary-600' : ''
                }`}
              >
                Bulk Data Export
              </Link>
              <Link
                to="/input-builder"
                className={`border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                  location.pathname === '/input-builder' ? 'border-primary-500 text-primary-600' : ''
                }`}
              >
                Input Builder
              </Link>
              <Link
                to="/dashboard"
                className={`border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                  location.pathname === '/dashboard' ? 'border-primary-500 text-primary-600' : ''
                }`}
              >
                Patient Dashboard
              </Link>
              <Link
                to="/hl7-connectors"
                className={`border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                  location.pathname === '/hl7-connectors' ? 'border-primary-500 text-primary-600' : ''
                }`}
              >
                HL7 Connectors
              </Link>
            </div>
          </div>
          
          {/* System Switcher */}
          <div className="flex items-center">
            <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => handleSystemSwitch('epic')}
                disabled={isSwitching}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  currentSystem === 'epic'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                } ${isSwitching ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Epic
              </button>
              <button
                onClick={() => handleSystemSwitch('cerner')}
                disabled={isSwitching}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  currentSystem === 'cerner'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                } ${isSwitching ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Cerner
              </button>
            </div>
            {isSwitching && (
              <div className="ml-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

function App() {
  useEffect(() => {
    document.title = 'AI4Health AI Studio';
  }, []);

  return (
    <Router>
      <Routes>
        <Route path="/dashboard/:patientId" element={<PatientDashboard />} />
        <Route path="/dashboard" element={<PatientDashboard />} />
        <Route path="/*" element={
          <div className="min-h-screen bg-gray-100 flex flex-col">
            <Navigation />
            <div className="flex-grow">
              <Routes>
                <Route path="/" element={<MainApp />} />
                <Route path="/bulk-data" element={<BulkDataExport />} />
                <Route path="/input-builder" element={<InputBuilder />} />
                <Route path="/hl7-connectors" element={<HL7ConnectorManager />} />
              </Routes>
            </div>
            <Footer />
          </div>
        } />
      </Routes>
    </Router>
  );
}

export default App;
